import { useState, useEffect } from "react";
import { EntityLink } from "@/components/shared/EntityLink";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import EntityContextPanel from "@/components/shared/EntityContextPanel";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { isProductionDepartment } from "@apv-erp/status";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { NumericInput } from "@/components/ui/numeric-input";
import { useQueryClient } from "@tanstack/react-query";
import { formatNumber, cn } from "@/lib/utils";
import { Zap, Clock, User, Layers, CheckCircle2, ScanBarcode, Lock } from "lucide-react";
import { ShopfloorScannerModal } from "@/components/factory/ShopfloorScannerModal";
import { useAuthStore } from "@/stores/auth.store";

import { toVnDateTimeLocal } from "@apv-erp/status";
const productionLogSchema = z.object({
  productionOrderId: z.string().min(1, "Vui lòng chọn lệnh sản xuất"),
  routingStepId: z.string().min(1, "Vui lòng chọn công đoạn"),
  machineId: z.string().optional().nullable(),
  operatorId: z.string().optional().nullable(),
  shift: z.string().optional().nullable(),
  qtyOk: z.coerce.number().min(0, "Số lượng không hợp lệ"),
  qtyNg: z.coerce.number().min(0, "Số lượng không hợp lệ"),
  defectReason: z.string().optional().nullable(),
  runStartTime: z.string().optional().nullable(),
  runEndTime: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

type ProductionLogFormValues = z.infer<typeof productionLogSchema>;

export interface ProductionLogPrefill {
  runStartTime?: string;
  productionOrderId: string;
  poCode?: string;
  productName?: string;
  customerName?: string;
  routingStepId: string;
  stepName?: string;
  stepOrder?: number;
  machineId?: string;
  machineName?: string;
  standardQuantity?: number;
  actualQtyOk?: number;
  remainingQty?: number;
  productionUnit?: string;
}

export interface ProductionLogFormProps {
  isModal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialData?: any;
  prefill?: ProductionLogPrefill;
  processType?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function formatLocalDatetimeInput(dateInput: Date | number | string): string {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "";
  return toVnDateTimeLocal(d);
}

export function ProductionLogForm({
  isModal = true,
  open = true,
  onOpenChange,
  initialData,
  prefill,
  processType,
  onSuccess,
  onCancel,
}: ProductionLogFormProps) {
  const [loading, setLoading] = useState(false);
  const [routingSteps, setRoutingSteps] = useState<any[]>([]);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  // Tự nhận diện người thực hiện chỉ khi người đăng nhập thuộc phòng sản xuất (quản trị / kế hoạch ghi hộ thì chọn thợ)
  const selfOperatorId = currentUser?.id && isProductionDepartment((currentUser as any)?.department) ? currentUser.id : "";

  const form = useForm<ProductionLogFormValues>({
    resolver: zodResolver(productionLogSchema),
    defaultValues: {
      productionOrderId: "",
      routingStepId: "",
      machineId: "",
      operatorId: selfOperatorId || "",
      shift: "Ca 1",
      qtyOk: 0,
      qtyNg: 0,
      defectReason: "",
      runStartTime: formatLocalDatetimeInput(Date.now() - 60 * 60 * 1000),
      runEndTime: formatLocalDatetimeInput(new Date()),
      notes: "",
    },
  });

  const selectedPoId = form.watch("productionOrderId");
  // Công đoạn đang chọn quyết định máy / người thực hiện được chọn (lệnh → công đoạn → máy, 03/11/2026)
  const selectedStepId = form.watch("routingStepId");
  const watchStartTime = form.watch("runStartTime");
  const watchEndTime = form.watch("runEndTime");

  let runDurationMinutes = 0;
  if (watchStartTime && watchEndTime) {
    const start = new Date(watchStartTime).getTime();
    const end = new Date(watchEndTime).getTime();
    if (end > start) {
      runDurationMinutes = Math.round((end - start) / 60000);
    }
  }

  // Fetch routing steps when PO changes (in non-prefill mode)
  useEffect(() => {
    if (selectedPoId && !prefill) {
      trpcClient.productionOrders.getById
        .query({ id: selectedPoId })
        .then((res: any) => {
          if (res && res.routingSteps) {
            let steps = res.routingSteps;
            if (processType && processType !== "ALL") {
              const filtered = res.routingSteps.filter(
                (s: any) => s.process && s.process.type === processType,
              );
              if (filtered.length > 0) {
                steps = filtered;
              }
            }
            setRoutingSteps(steps || []);
          } else {
            setRoutingSteps([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching routing steps", err);
          setRoutingSteps([]);
        });
    } else if (!selectedPoId) {
      setRoutingSteps([]);
    }
  }, [selectedPoId, processType, prefill]);

  useEffect(() => {
    if (selfOperatorId && !form.getValues("operatorId")) {
      form.setValue("operatorId", selfOperatorId);
    }
  }, [selfOperatorId, form]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const now = new Date();
      const nowString = formatLocalDatetimeInput(now);
      const defaultStartString = formatLocalDatetimeInput(now.getTime() - 60 * 60 * 1000);
      if (prefill) {
        form.reset({
          productionOrderId: prefill.productionOrderId,
          routingStepId: prefill.routingStepId,
          machineId: prefill.machineId || "",
          operatorId: selfOperatorId || "",
          shift: "Ca 1",
          qtyOk: prefill.remainingQty !== undefined && prefill.remainingQty > 0 ? prefill.remainingQty : 0,
          qtyNg: 0,
          defectReason: "",
          runStartTime: prefill.runStartTime ? formatLocalDatetimeInput(prefill.runStartTime) : defaultStartString,
          runEndTime: nowString,
          notes: "",
        });
      } else if (initialData) {
        form.reset({
          productionOrderId: initialData.productionOrderId || "",
          routingStepId: initialData.routingStepId || initialData.stepId || "",
          machineId: initialData.machineId || "",
          operatorId: initialData.operatorId || selfOperatorId || "",
          shift: initialData.shift || "Ca 1",
          qtyOk: initialData.qtyOk || 0,
          qtyNg: initialData.qtyNg || 0,
          defectReason: initialData.defectReason || "",
          runStartTime: initialData.runStartTime
            ? formatLocalDatetimeInput(initialData.runStartTime)
            : defaultStartString,
          runEndTime: initialData.runEndTime
            ? formatLocalDatetimeInput(initialData.runEndTime)
            : nowString,
          notes: initialData.notes || "",
        });
      } else {
        form.reset({
          productionOrderId: "",
          routingStepId: "",
          machineId: "",
          operatorId: selfOperatorId || "",
          shift: "Ca 1",
          qtyOk: 0,
          qtyNg: 0,
          defectReason: "",
          runStartTime: defaultStartString,
          runEndTime: nowString,
          notes: "",
        });
      }
    }
  }, [open, initialData, prefill, currentUser, form]);

  const onSubmit = async (values: ProductionLogFormValues) => {
    try {
      if (values.runStartTime && values.runEndTime && new Date(values.runEndTime) < new Date(values.runStartTime)) {
        toast.error("Giờ kết thúc không thể trước giờ bắt đầu làm việc!");
        return;
      }
      setLoading(true);
      const nowIso = new Date().toISOString();
      const runStartTimeIso = values.runStartTime
        ? new Date(values.runStartTime).toISOString()
        : nowIso;
      const runEndTimeIso = values.runEndTime
        ? new Date(values.runEndTime).toISOString()
        : nowIso;

      const submitData = {
        productionOrderId: values.productionOrderId,
        routingStepId: values.routingStepId,
        machineId: values.machineId || undefined,
        operatorId: values.operatorId || currentUser?.id || undefined,
        shift: values.shift || undefined,
        qtyOk: Number(values.qtyOk) || 0,
        qtyNg: Number(values.qtyNg) || 0,
        defectReason: values.defectReason || undefined,
        notes: values.notes || undefined,
        date: nowIso,
        runStartTime: runStartTimeIso,
        runEndTime: runEndTimeIso,
      };

      if (initialData?.id) {
        await trpcClient.productionLogs.update.mutate({
          id: initialData.id,
          ...submitData,
        });
        toast.success("Cập nhật nhật ký sản xuất thành công!");
      } else {
        await trpcClient.productionLogs.create.mutate(submitData);
        toast.success("Ghi nhận sản lượng sản xuất thành công!");
      }

      // Invalidate queries across both Page 1 and Page 2 and details
      queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["production-planning"] });
      queryClient.invalidateQueries({ queryKey: ["production-logs"] });
      queryClient.invalidateQueries({ queryKey: ["production-orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });

      onSuccess?.();
      onOpenChange?.(false);
    } catch (error: any) {
      toast.error(error.message || "Có lỗi xảy ra khi lưu nhật ký");
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <div className={cn("space-y-4", !isModal && "bg-white border border-slate-200 rounded-lg p-5 shadow-xs")}>
      {!isModal && (
        <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
          <Zap className="w-5 h-5 text-amber-500 fill-amber-500" />
          <h2 className="text-base font-bold text-slate-800">
            {initialData
              ? "Cập nhật nhật ký sản xuất"
              : prefill
                ? "Ghi nhận sản lượng công đoạn"
                : "Thêm mới nhật ký sản xuất"}
          </h2>
        </div>
      )}

      {/* Context Banner if opened with prefill */}
        {prefill && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-teal-100 text-teal-800 uppercase tracking-wider">
                  Kế Hoạch Công Đoạn
                </span>
                <span className="font-mono font-bold text-sm text-slate-800">
                  {prefill.poCode}
                </span>
              </div>
              {prefill.customerName && (
                <span className="text-xs text-muted-foreground truncate max-w-[220px]">
                  {prefill.customerName}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-700">
              Sản phẩm: <span className="font-semibold">{prefill.productName || "—"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs pt-1.5 border-t border-slate-200">
              <div>
                <span className="text-muted-foreground">Công đoạn:</span>{" "}
                <span className="font-semibold text-slate-800">
                  {prefill.stepOrder ? `Bước ${prefill.stepOrder}: ` : ""}
                  {prefill.stepName}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Kế hoạch:</span>{" "}
                <span className="font-mono font-bold text-slate-800">
                  {formatNumber(prefill.standardQuantity || 0)} {prefill.productionUnit || "tờ"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Đã đạt:</span>{" "}
                <span className="font-mono font-bold text-emerald-600">
                  {formatNumber(prefill.actualQtyOk || 0)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Còn lại:</span>{" "}
                <span className="font-mono font-bold text-amber-600">
                  {formatNumber(prefill.remainingQty || 0)}
                </span>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-1">
            {/* If NOT in prefill mode: Cascading selectors */}
            {!prefill && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <FormField
                  control={form.control}
                  name="productionOrderId"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-xs font-semibold">Lệnh sản xuất *</FormLabel>
                        {!initialData?.id && <button
                          type="button"
                          onClick={() => setBarcodeModalOpen(true)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer"
                        >
                          <ScanBarcode className="w-3.5 h-3.5 text-indigo-600" />
                          <span>Quét mã LSX</span>
                        </button>}
                      </div>
                      <FormControl>
                        <SharedCombobox
                          type="productionOrder"
                          value={field.value || ""}
                          onChange={(v: string) => {
                            field.onChange(v);
                            form.setValue("routingStepId", "");
                          }}
                          placeholder="Chọn lệnh sản xuất..."
                          // Nhật ký đã lưu gắn cố định với lệnh / công đoạn (sản lượng đã cộng vào lệnh đó); ghi nhầm thì xoá và nhập lại
                          disabled={Boolean(initialData?.id)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="routingStepId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold">Công đoạn kế hoạch *</FormLabel>
                      <Select
                        onValueChange={(val) => {
                          field.onChange(val);
                          const step = routingSteps.find((s) => s.id === val);
                          // Đổi công đoạn → máy theo kế hoạch của công đoạn đó (không giữ máy của công đoạn trước)
                          form.setValue("machineId", step?.machineId || "");
                        }}
                        value={field.value}
                        disabled={!selectedPoId || Boolean(initialData?.id)}
                      >
                        <FormControl>
                          <SelectTrigger className="h-9 text-xs">
                            <SelectValue placeholder="Chọn công đoạn..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {routingSteps.map((step) => (
                            <SelectItem key={step.id} value={step.id} className="text-xs">
                              {step.stepOrder ? `Bước ${step.stepOrder}: ` : ""}
                              {step.process?.name || step.name || "Công đoạn"}
                              {step.standardQuantity ? ` (${formatNumber(step.standardQuantity)} tờ)` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Machine Row: Full width roomy */}
            <FormField
              control={form.control}
              name="machineId"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel className="text-xs font-semibold">Máy sản xuất *</FormLabel>
                  <FormControl>
                    <SharedCombobox
                      type="machine"
                      value={field.value || ""}
                      onChange={(id) => field.onChange(id)}
                      // Chỉ máy đúng loại của công đoạn (giữ máy theo kế hoạch); chưa chọn công đoạn thì khoá
                      params={selectedStepId ? { routingStepId: selectedStepId } : undefined}
                      disabled={!selectedStepId}
                      placeholder={selectedStepId ? "Chọn máy thực hiện..." : "Chọn công đoạn trước"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Operator and Shift Row: 2/3 and 1/3 ratio with min-w-0 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 items-start">
              <div className="sm:col-span-2 min-w-0">
                <FormField
                  control={form.control}
                  name="operatorId"
                  render={({ field }) => (
                    <FormItem className="min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                        <FormLabel className="text-xs font-semibold flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span className="truncate">Người thực hiện *</span>
                        </FormLabel>
                        {currentUser && field.value === currentUser.id && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-medium bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 self-start sm:self-auto shrink-0">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span className="truncate max-w-[120px]">Nhận diện tự động</span>
                          </span>
                        )}
                      </div>
                      <FormControl>
                        <SharedCombobox
                          type="user"
                          // Chỉ nhân sự phòng sản xuất (thợ có thể hỗ trợ tổ khác); trước đây lọc role=OPERATOR nên chỉ ra 1 người
                          params={{ scope: "production" }}
                          value={field.value || ""}
                          onChange={(id) => field.onChange(id)}
                          placeholder="Chọn thợ máy thực hiện..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="min-w-0">
                <FormField
                  control={form.control}
                  name="shift"
                  render={({ field }) => (
                    <FormItem className="min-w-0">
                      <FormLabel className="text-xs font-semibold mb-1 block">Ca làm việc</FormLabel>
                      <FormControl>
                        <ComboboxConfigMaster
                          categoryCode="shifts"
                          valueField="name"
                          value={field.value || "Ca 1"}
                          onChange={field.onChange}
                          placeholder="Chọn ca..."
                          hideLabel
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Time Tracking Row: Editable & Real-time Assisted */}
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                  <Clock className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Thời gian sản xuất thực tế</span>
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  <span>Có thể chỉnh sửa theo thực tế xưởng</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="runStartTime"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-[11px] text-slate-700 font-medium">
                          Giờ bắt đầu làm việc:
                        </FormLabel>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              const endVal = form.getValues("runEndTime") || formatLocalDatetimeInput(new Date());
                              const endMs = new Date(endVal).getTime();
                              const newStart = formatLocalDatetimeInput(endMs - 30 * 60 * 1000);
                              field.onChange(newStart);
                            }}
                            className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded border border-indigo-200 transition-colors"
                          >
                            -30p
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const endVal = form.getValues("runEndTime") || formatLocalDatetimeInput(new Date());
                              const endMs = new Date(endVal).getTime();
                              const newStart = formatLocalDatetimeInput(endMs - 60 * 60 * 1000);
                              field.onChange(newStart);
                            }}
                            className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded border border-indigo-200 transition-colors"
                          >
                            -1h
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const endVal = form.getValues("runEndTime") || formatLocalDatetimeInput(new Date());
                              const endMs = new Date(endVal).getTime();
                              const newStart = formatLocalDatetimeInput(endMs - 120 * 60 * 1000);
                              field.onChange(newStart);
                            }}
                            className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded border border-indigo-200 transition-colors"
                          >
                            -2h
                          </button>
                        </div>
                      </div>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          className="h-8 text-xs font-mono bg-white text-slate-800 border-slate-300 focus:border-indigo-500"
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="runEndTime"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-[11px] text-slate-700 font-medium">
                          Giờ kết thúc / Ghi nhận:
                        </FormLabel>
                        <button
                          type="button"
                          onClick={() => {
                            field.onChange(formatLocalDatetimeInput(new Date()));
                          }}
                          className="text-[10px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded border border-slate-300 transition-colors"
                        >
                          Bây giờ
                        </button>
                      </div>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          className="h-8 text-xs font-mono bg-white text-slate-800 border-slate-300 focus:border-indigo-500"
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {runDurationMinutes > 0 ? (
                <div className="text-[11px] text-slate-700 pt-1 border-t border-slate-200 flex items-center justify-between">
                  <span>Thời lượng chạy máy: <strong className="text-emerald-700 font-mono font-bold">{Math.floor(runDurationMinutes / 60)}h {runDurationMinutes % 60}m ({runDurationMinutes} phút)</strong></span>
                  <span className="text-slate-500 text-[10px]">✓ Hợp lệ</span>
                </div>
              ) : (
                <div className="text-[11px] text-amber-700 pt-1 border-t border-slate-200 flex items-center justify-between">
                  <span>⚠️ Giờ bắt đầu trùng hoặc sau giờ kết thúc. Hãy điều chỉnh thời gian làm việc thực tế.</span>
                </div>
              )}
            </div>

            {/* Output Quantities Row: Highlighted */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 p-3 rounded-lg bg-emerald-50/50 border border-emerald-200">
              <FormField
                control={form.control}
                name="qtyOk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Số lượng đạt (OK) *
                    </FormLabel>
                    <FormControl>
                      <NumericInput
                        className="h-9 text-sm font-mono font-bold text-emerald-700 bg-white border-emerald-300"
                        value={field.value ?? 0}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="qtyNg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold text-rose-800">
                      Số lượng hỏng / Phế phẩm (NG)
                    </FormLabel>
                    <FormControl>
                      <NumericInput
                        className="h-9 text-sm font-mono font-bold text-rose-600 bg-white border-rose-300"
                        value={field.value ?? 0}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Defect reason & notes — min-w-0 để ô chọn có gợi ý dài không đẩy hộp thoại tràn màn điện thoại */}
            <div className="grid grid-cols-1 gap-3 [&>*]:min-w-0">
              <FormField
                control={form.control}
                name="defectReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold">Lý do lỗi / Ghi nhận sự cố</FormLabel>
                    <FormControl>
                      <ComboboxConfigMaster
                        categoryCode="defect_reasons"
                        valueField="name"
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Chọn hoặc thêm lý do lỗi"
                        hideLabel
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold">Ghi chú vận hành</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        className="text-xs"
                        placeholder="Ghi chú thêm thông tin cho ca sau hoặc quản đốc..."
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  } else if (onOpenChange) {
                    onOpenChange?.(false);
                  }
                }}
                disabled={loading}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                disabled={loading}
              >
                {loading ? "Đang lưu..." : initialData ? "Cập Nhật Nhật Ký" : "Lưu Nhật Ký Sản Xuất"}
              </Button>
            </div>
          </form>
        </Form>

        {/* Nhật ký đã có: liên kết tới lệnh / máy / thợ, thảo luận - đính kèm - lịch sử, người tạo / cập nhật */}
        {initialData?.id && (
          <div className="mt-4 pt-3 border-t border-slate-200 space-y-3" data-testid="production-log-context">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
              {(initialData.productionOrder?.orderId || initialData.productionOrderId) && (
                <span>
                  Lệnh SX:{" "}
                  <EntityLink
                    entityType="productionOrder"
                    id={initialData.productionOrder?.orderId || initialData.productionOrderId}
                    code={initialData.productionOrder?.orderId}
                  />
                </span>
              )}
              {initialData.machineId && (
                <span>
                  Máy: <EntityLink entityType="machine" id={initialData.machineId} label={initialData.machine?.name || initialData.machineName} />
                </span>
              )}
              {initialData.operatorId && (
                <span>
                  Thợ: <EntityLink entityType="user" id={initialData.operatorId} label={initialData.operator?.fullName || initialData.operatorName} />
                </span>
              )}
            </div>
            <EntityContextPanel entityType="production_log" entityId={initialData.id} />
            <EntityAuditFooter entityType="production_log" entityId={initialData.id} fallback={{ created: initialData.createdAt, updated: initialData.updatedAt }} />
          </div>
        )}

        {/* Barcode Scanner Modal for Quick PO Selection */}
        {barcodeModalOpen && (
          <ShopfloorScannerModal
            open={barcodeModalOpen}
            onOpenChange={setBarcodeModalOpen}
            onSelectPo={(poId) => {
              form.setValue("productionOrderId", poId);
              form.setValue("routingStepId", "");
              setBarcodeModalOpen(false);
            }}
            onOpenLogForm={(pref) => {
              form.setValue("productionOrderId", pref.productionOrderId);
              form.setValue("routingStepId", pref.routingStepId);
              if (pref.machineId) form.setValue("machineId", pref.machineId);
              if (pref.remainingQty !== undefined && pref.remainingQty > 0) {
                form.setValue("qtyOk", pref.remainingQty);
              }
              setBarcodeModalOpen(false);
            }}
          />
        )}
    </div>
  );

  if (!isModal) {
    return formContent;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500 fill-amber-500" />
            {initialData
              ? "Cập nhật nhật ký sản xuất"
              : prefill
                ? "Ghi nhận sản lượng công đoạn (1-chạm)"
                : "Thêm mới nhật ký sản xuất"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Biểu mẫu ghi nhận số lượng đạt, phế phẩm và nhật ký máy cho công đoạn
          </DialogDescription>
        </DialogHeader>

        {formContent}
      </DialogContent>
    </Dialog>
  );
}

export default ProductionLogForm;
